import { AddressModel, AddressModelFromJson } from '../../models/complex/address.model';

/**
 * Feature key under which the address slice is registered in the root store
 * (mirrors the 'timesheet' / 'config' feature keys).
 */
export const ADDRESS_FEATURE_KEY = 'address';

export type AddressStatus = 'idle' | 'syncing' | 'ready';

/**
 * Serializable address slice.
 *
 * The store is the *source of truth*: writes set the collection in the slice first, and the
 * generic persistence layer then mirrors it to the shared encrypted+compressed file storage
 * (keyed by the address feature). Storage is read only once, on init, to rehydrate the slice.
 * State holds the plain `JsonAddressStateModel` shape only, satisfying the root store's
 * `strictStateSerializability` runtime check; selectors rehydrate to `AddressModel` for
 * consumers.
 */
// `error` uses `null` instead of the repo's usual `undefined`: the root store's
// strict serializability runtime checks reject explicitly undefined values.
export type AddressCollectionState = Readonly<{
    addresses: AddressState[];
    status: AddressStatus;
    error: string | null;
}>;

export const initialAddressCollectionState: AddressCollectionState = {
    addresses: [],
    status: 'idle',
    error: null,
};

export type TimesheetAddressState = {
    street: string;
    city: string;
    postCode: string;
    country: string;
    addrText: string;
    addrNumber: string;
    stateOfUSA?: string;
    addrKey?: string;
    allowToHide: boolean;
    id: string;
};

export type AddressState = TimesheetAddressState & {
    name1: string;
    name2: string;
    name3: string;
    name4: string;
    name1shortP58: string;
    name2shortP58: string;
    building: string;
    room: string;
    floor: string;
    department: string;
    contactName1: string;
    contactName2: string;
    contactPhone: string;
    contactType: string;
    email: string;
    phone: string;
    fax1: string;
    fax2: string;
    fax3: string;
    fax4: string;
    state: string;
    visible: boolean;
    isFromAddressSet: boolean;
    allowToHide: boolean;
    id: string;
    /**
     * Whether the address book has deleted this address — a tombstone, not a removal.
     *
     * A timesheet entry now stores only an address *id* (see `TimesheetEntryState.address`), so a
     * deleted address may not simply disappear from the collection: doing so would strand every
     * entry that still points at it, including an already-submitted timesheet's, whose address must
     * keep resolving for the legacy model bridge (`convertTimesheetStateToTimesheetModel`) and the
     * submit out-model (`buildTimesheetOutModel`). Tombstoning keeps the id resolvable while taking
     * the address out of the pickers (`selectVisibleAddresses`) and out of the restore-hidden count
     * (`selectHasHiddenAddresses`). A backend re-send of the address clears it — see `upsertAddress`.
     */
    deleted: boolean;
    /**
     * Whether this address is backend-supplied but was not part of the most recent `NdpHeaderSet` —
     * out of scope for the pickers, not gone.
     *
     * Only a customer address or an `AddressSet` row is ever marked this way: a user-created address
     * (`isUserCreatedAddress`) is never in the backend's gift to include or omit, so it is exempt and
     * always carries `false` (see `markAddressesOutOfScope`). Cleared the moment the address arrives
     * again in a fresh `NdpHeaderSet` (`upsertAddress`), so nothing is lost — this is a visibility
     * scope, not a decision about the address itself.
     *
     * Deliberately its own flag rather than reusing either of the two that already keep an address
     * out of the pickers: not `deleted`, because the address book never deleted it — the backend
     * simply stopped sending it, which is a different fact with a different owner; and not
     * `visible: false`, because the "restore hidden addresses" affordance (`resetAddressVisibility`)
     * must not bring back an address the backend is not currently offering (see
     * `selectHasHiddenAddresses`). `selectVisibleAddresses` excludes it for the same reason it
     * excludes a tombstone.
     */
    outOfScope: boolean;
};

/**
 * The address fields the address book lets a technician type, and so the only ones an edit may
 * change. Everything else an address carries — its identity, its address number, the contact and
 * naming extras a backend address brings — is not the form's to say.
 *
 * `stateOfUSA` is the one field narrowed against {@link AddressState}, where it is optional: a form
 * field always holds a value, and "no state chosen" is the empty string. It reaches the collection
 * as `''` rather than `undefined`, which every reader already treats alike (see `cityWithUsState`
 * and `toJsonTimesheetAddress`).
 */
export type AddressEdit = Pick<AddressState, 'name1' | 'street' | 'city' | 'postCode' | 'country' | 'addrText'> & {
    stateOfUSA: string;
};

export const initialAddressState: AddressState = {
    addrKey: undefined,
    addrNumber: '',
    addrText: '',
    allowToHide: false,
    building: '',
    city: '',
    contactName1: '',
    contactName2: '',
    contactPhone: '',
    contactType: '',
    country: '',
    deleted: false,
    department: '',
    email: '',
    fax1: '',
    fax2: '',
    fax3: '',
    fax4: '',
    floor: '',
    isFromAddressSet: false,
    name1: '',
    name1shortP58: '',
    name2: '',
    name2shortP58: '',
    name3: '',
    name4: '',
    outOfScope: false,
    phone: '',
    postCode: '',
    room: '',
    id: '',
    state: '',
    stateOfUSA: undefined,
    street: '',
    visible: false,
};

/**
 * The collection with the `deleted` and `outOfScope` invariants restored: every address explicitly
 * carries both flags, each defaulted to `false` for a collection persisted before it existed (which
 * has none of it at all, despite the type saying otherwise — storage is outside the compiler's
 * guarantees). The two are handled together — one pass, one function — because they are the same
 * shape of problem arriving at different times: a boolean bookkeeping flag storage might not have,
 * where `false` is a legitimate value and not "missing".
 *
 * A sibling to `withAddressNumbers` rather than folded into it: that function's fast path already
 * balances two invariants (id, addrNumber) against each other via a single truthy check, and a
 * third kind of invariant — a boolean rather than a non-empty string — would only muddy it. Kept as
 * a distinct pass so each stays a one-invariant-shape, one-reason-to-change function. Applied in the
 * same place — `setAddressesReducer`, the slice's single entry point for any collection — so
 * hydrating a collection that predates either flag cannot leave it as `undefined` for a selector or
 * the root store's `strictStateSerializability` check to trip over.
 *
 * Mirrors `withAddressNumbers`'s fast-path property: a collection that already satisfies the
 * invariant is returned by reference, unchanged, and so is every address within it that already did.
 *
 * @param addresses the collection about to enter the slice
 */
export function withFlagDefaults(addresses: AddressState[]): AddressState[] {
    if (addresses.every((address) => address.deleted !== undefined && address.outOfScope !== undefined)) {
        return addresses;
    }
    return addresses.map((address) =>
        address.deleted !== undefined && address.outOfScope !== undefined
            ? address
            : { ...address, deleted: address.deleted ?? false, outOfScope: address.outOfScope ?? false },
    );
}

/**
 * Serializes rehydrated address models to the slice's JSON shape, preserving the
 * `allowToHide` flag, the tombstone and scoping flags, and the address identity alongside the
 * persisted `toJSON()` fields. Shared by the load effect and the facade's write path.
 *
 * `deleted` and `outOfScope` are carried through explicitly, exactly like `allowToHide` and `id`:
 * they are slice bookkeeping `AddressModel.toJSON()` deliberately omits (see its doc), so a caller
 * that built this from `toJSON()` alone would silently wipe every tombstone and scope decision on
 * the next write.
 *
 * @param addresses the address models to serialize
 * @returns the plain, store-safe representation of the collection
 */
export const toAddressState = (addresses: AddressModel[]): AddressState[] =>
    addresses.map((address) => {
        const { stateOfUSA, ...json } = address.toJSON();
        return {
            ...json,
            // omit an unset stateOfUSA: strict serializability rejects undefined values
            ...(stateOfUSA !== undefined ? { stateOfUSA } : {}),
            allowToHide: address.allowToHide,
            id: address.id,
            deleted: address.deleted,
            outOfScope: address.outOfScope,
        };
    });

/**
 * Rehydrates the slice's JSON shape back into address models, restoring the
 * fields (`allowToHide`, `id`, `deleted`, `outOfScope`) that `AddressModelFromJson` regenerates.
 * Shared by the address selectors and the merge effect.
 *
 * @param addresses the slice-shaped collection to rehydrate
 * @returns the address models
 */
export const fromAddressState = (addresses: AddressState[]): AddressModel[] =>
    addresses.map((a) => {
        const address = new AddressModelFromJson(a);
        address.allowToHide = a.allowToHide ?? false;
        // fall back to the freshly minted one for slice states predating the id
        address.id = a.id ?? address.id;
        // collections persisted before this field existed carry no `deleted` at all
        address.deleted = a.deleted ?? false;
        // collections persisted before this field existed carry no `outOfScope` at all
        address.outOfScope = a.outOfScope ?? false;
        return address;
    });
